import { useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { GoldBtn, Particles, MaskedLine, Magnetic } from "@/components/Shared";

const EASE = [0.22, 1, 0.36, 1];

export default function Hero({ started }) {
    const d = started ? 0 : 5.4;
    const ref = useRef(null);
    const [tilt, setTilt] = useState({ x: 0, y: 0 });
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const yEmblem = useTransform(scrollYProgress, [0, 1], [0, 140]);
    const yText = useTransform(scrollYProgress, [0, 1], [0, 70]);
    const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

    const onMove = (e) => {
        const r = e.currentTarget.getBoundingClientRect();
        setTilt({
            x: (e.clientX - r.left) / r.width - 0.5,
            y: (e.clientY - r.top) / r.height - 0.5,
        });
    };

    return (
        <section
            ref={ref}
            className="relative min-h-screen flex items-center justify-center overflow-hidden noise"
            data-testid="hero-section"
            onMouseMove={onMove}
            onMouseLeave={() => setTilt({ x: 0, y: 0 })}
        >
            <div className="absolute inset-0 pointer-events-none">
                <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse 90% 60% at 50% -10%, rgba(92,16,28,0.75), transparent 60%)" }} />
                <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse 60% 45% at 50% 110%, rgba(38,8,16,0.9), transparent 65%)" }} />
                <div className="absolute inset-0 jaali opacity-30" />
                <div
                    className="absolute left-1/2 top-1/2 w-[54rem] h-[54rem] rounded-full opacity-[0.14] spin-slow hidden md:block parallax-el"
                    style={{
                        background: "conic-gradient(from 0deg, transparent 0deg, rgba(201,162,75,0.5) 20deg, transparent 40deg, transparent 90deg, rgba(201,162,75,0.3) 110deg, transparent 130deg, transparent 360deg)",
                        transform: `translate(calc(-50% + ${tilt.x * -30}px), calc(-50% + ${tilt.y * -22}px))`,
                    }}
                />
            </div>
            <Particles density={75} />

            <svg className="absolute inset-x-0 bottom-0 mx-auto h-[52vh] opacity-[0.2] hidden sm:block" viewBox="0 0 1200 300" fill="none" preserveAspectRatio="xMidYMax meet" aria-hidden="true">
                {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => {
                    const x = 80 + i * 148;
                    return <path key={i} d={`M${x} 300 V120 Q${x} 60 ${x + 55} 60 Q${x + 110} 60 ${x + 110} 120 V300`} stroke="var(--gold)" strokeWidth="0.8" />;
                })}
                <line x1="0" y1="299" x2="1200" y2="299" stroke="var(--gold)" strokeWidth="1" className="dash-flow" />
            </svg>

            <motion.div style={{ opacity: fade }} className="container-x relative text-center pt-36 pb-24 md:pt-40">
                <MaskedLine delay={d + 0.1} className="flex justify-center" innerClass="eyebrow">
                    India's Grand Festival of Startups, Innovation &amp; Entrepreneurship
                </MaskedLine>

                <motion.div style={{ y: yEmblem }} className="relative inline-block mt-10 mb-9">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.84, filter: "blur(12px)" }}
                        animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
                        transition={{ delay: d + 0.25, duration: 1.5, ease: EASE }}
                    >
                        <div className="emblem-frame">
                            <div className="parallax-el" style={{ transform: `translate3d(${tilt.x * 20}px, ${tilt.y * 14}px, 0)` }}>
                                <img src="/logo.png" alt="Startup MahaBazaar 2027 — official emblem" className="w-32 sm:w-44 md:w-52 mx-auto floaty drop-shadow-[0_20px_60px_rgba(165,28,48,0.45)]" />
                            </div>
                        </div>
                    </motion.div>
                </motion.div>

                <h1 className="font-display font-medium leading-[0.95]">
                    <MaskedLine delay={d + 0.5} innerClass="text-lg sm:text-2xl tracking-[0.65em] text-[var(--champagne)]">
                        STARTUP
                    </MaskedLine>
                    <span className="block mt-3 whitespace-nowrap" aria-label="MAHABAZAAR">
                        {"MAHABAZAAR".split("").map((ch, i) => (
                            <span key={i} className="inline-block overflow-hidden align-bottom" aria-hidden="true">
                                <motion.span
                                    className="inline-block gold-text font-semibold"
                                    style={{ fontSize: "clamp(2rem, 10vw, 8rem)", lineHeight: 1.05 }}
                                    initial={{ y: "112%" }}
                                    animate={{ y: 0 }}
                                    transition={{ delay: d + 0.62 + i * 0.05, duration: 1, ease: EASE }}
                                >
                                    {ch}
                                </motion.span>
                            </span>
                        ))}
                    </span>
                    <MaskedLine delay={d + 1.35} className="mt-5" innerClass="flex items-center justify-center gap-6">
                        <span className="h-px w-16 sm:w-28 bg-gradient-to-r from-transparent to-[var(--gold)]" />
                        <span className="text-2xl sm:text-4xl tracking-[0.5em] text-[var(--ivory)]">2027</span>
                        <span className="h-px w-16 sm:w-28 bg-gradient-to-l from-transparent to-[var(--gold)]" />
                    </MaskedLine>
                </h1>

                <MaskedLine delay={d + 1.55} className="mt-7" innerClass="font-display italic text-lg sm:text-2xl text-[var(--champagne)]/90">
                    Bazaar of Ideas, Innovation &amp; Impact
                </MaskedLine>

                <MaskedLine delay={d + 1.7} className="mt-7" innerClass="flex items-center justify-center gap-4 text-[0.62rem] sm:text-xs tracking-[0.34em] uppercase text-muted-luxe">
                    <span>29–31 January 2027</span>
                    <span className="w-1.5 h-1.5 rotate-45 bg-[var(--gold)]" />
                    <span>MSIT &amp; MSI, New Delhi</span>
                </MaskedLine>

                <motion.div
                    style={{ y: yText }}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: d + 1.9, duration: 1 }}
                    className="mt-11 flex flex-wrap items-center justify-center gap-4"
                >
                    <Magnetic>
                        <GoldBtn to="/register" testid="hero-register-btn">Register Now</GoldBtn>
                    </Magnetic>
                    <Magnetic strength={12}>
                        <GoldBtn to="/experience" variant="outline" testid="hero-explore-btn">Explore the MahaBazaar</GoldBtn>
                    </Magnetic>
                    <Magnetic strength={12}>
                        <GoldBtn to="/exhibition" variant="dark" testid="hero-exhibit-btn">Exhibit With Us</GoldBtn>
                    </Magnetic>
                </motion.div>
            </motion.div>

            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: d + 2.4, duration: 1 }}
                className="absolute bottom-7 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-muted-luxe"
                aria-hidden="true"
            >
                <span className="text-[0.55rem] tracking-[0.4em] uppercase">Scroll</span>
                <ChevronDown size={15} className="pulse-soft text-[var(--gold)]" />
            </motion.div>

            <div className="absolute left-5 top-1/2 -translate-y-1/2 hidden lg:block" aria-hidden="true">
                <span className="block rotate-180 text-[0.55rem] tracking-[0.55em] uppercase text-[var(--gold)]/50" style={{ writingMode: "vertical-rl" }}>
                    Ideas · Innovation · Capital · Impact
                </span>
            </div>
        </section>
    );
}
